import { defineMessages } from "@/lib/i18n/messages";

export default defineMessages({
  es: {
    howToPlay: {
      title: "Cómo se juega la Sopa de letras",
      items: [
        {
          q: "¿Cómo se juega?",
          a: "Arrastra sobre las letras en línea recta (horizontal, vertical o diagonal, también al revés) para marcar cada palabra de la lista antes de que se acabe el tiempo.",
        },
        {
          q: "¿Cuántos niveles hay?",
          a: "Tres: fácil (15 palabras en 20 minutos), medio (20 palabras en 15 minutos) y difícil (30 palabras en 10 minutos). Pasas de nivel encontrando todas las palabras.",
        },
        {
          q: "¿Cuántos intentos tengo?",
          a: "3 al día, en un solo idioma. Cada partida usa uno, ganes o no; si se acaba el tiempo, reintentas el mismo nivel. Los intentos vuelven a medianoche de tu país.",
        },
        {
          q: "¿De dónde salen las palabras?",
          a: "De los eventos de los próximos días en tu país. Al terminar te mostramos los eventos de donde salieron.",
        },
        {
          q: "¿Hay ranking?",
          a: "Sí, uno por nivel: tu tiempo promedio y tu mejor tiempo, en el mundo o en tu país. Para aparecer necesitas una cuenta y sumarte al ranking.",
        },
      ],
    },
    title: "Sopa de letras",
    ogSubtitle: "Encuentra las palabras de los eventos de la semana. 3 niveles al día.",
    description:
      "Encuentra las palabras escondidas, sacadas de los eventos de esta semana. 3 niveles y 3 intentos al día: ¿llegas al difícil?",
    levelNames: { 1: "Fácil", 2: "Medio", 3: "Difícil" } as Record<number, string>,
    lives: (n: number) => (n === 1 ? "1 intento" : `${n} intentos`),
    levelSummary: (words: number, minutes: number) => `${words} palabras en ${minutes} minutos`,
    howTo:
      "Arrastra el dedo o el mouse sobre las letras para marcar cada palabra. Las palabras salen de eventos reales: al encontrarlas, te mostramos el evento.",
    lifeRule: "Tienes 3 intentos al día y cada partida usa uno, ganes o no. Si se acaba el tiempo, reintentas el mismo nivel.",
    start: (level: string) => `Empezar nivel ${level}`,
    starting: "Armando la sopa…",
    found: (n: number, total: number) => `${n} de ${total}`,
    timeLeft: "Tiempo",
    wordsTitle: "Por encontrar",
    foundTitle: "Encontradas",
    completedTitle: "¡Completado!",
    completedBody: (level: string) => `¡Has completado el nivel ${level}, felicidades!`,
    completedTime: (time: string) => `Lo resolviste en ${time}.`,
    playedInTitle: (language: string) => `Hoy ya jugaste en ${language}`,
    playedInBody: "Tienes 3 intentos al día, en un solo idioma. Puedes seguir allá o volver mañana.",
    playedInLink: (language: string) => `Seguir en ${language}`,
    continue: "Continuar",
    eventsTitle: "Eventos de tus palabras",
    carouselTitle: "De estos eventos salieron tus palabras",
    carouselSubtitle: "¿Te animas a ir?",
    eventsHint: "Se abren en otra pestaña: el reloj sigue corriendo.",
    lostTitle: "Se acabó el tiempo",
    lostBody: (found: number, total: number) => `Encontraste ${found} de ${total} palabras.`,
    nextUp: (level: string, lives: string) => `Sigue el nivel ${level}. Te quedan ${lives}.`,
    retry: (level: string, lives: string) => `Puedes reintentar el nivel ${level}. Te quedan ${lives}.`,
    allDoneTitle: "¡Completaste los 3 niveles de hoy!",
    allDoneBody: "Mañana hay sopas nuevas con los eventos de la semana.",
    noLivesTitle: "No te quedan intentos por hoy",
    livesBack: (time: string) => `Vuelven a medianoche (en ${time}).`,
    otherGame: "Mientras tanto, prueba La palabra del día",
    loadError: "No pudimos cargar la sopa de letras.",
    retryLoad: "Reintentar",
    actionError: "No pudimos guardar tu jugada. Revisa tu conexión.",
    conflict: "La partida cambió en otra pestaña; la actualizamos.",
    loginHint: "Inicia sesión para que tus intentos y tu progreso queden en tu cuenta.",
    login: "Iniciar sesión",
    gridLabel: "Sopa de letras",
  },
  en: {
    howToPlay: {
      title: "How to play Word search",
      items: [
        {
          q: "How do I play?",
          a: "Drag across the letters in a straight line (across, down or diagonal, also backwards) to mark each word on the list before time runs out.",
        },
        {
          q: "How many levels are there?",
          a: "Three: easy (15 words in 20 minutes), medium (20 words in 15 minutes) and hard (30 words in 10 minutes). You move up a level by finding every word.",
        },
        {
          q: "How many tries do I get?",
          a: "3 a day, in a single language. Each game uses one, win or lose; if time runs out, you retry the same level. Your tries come back at midnight in your country.",
        },
        {
          q: "Where do the words come from?",
          a: "From the upcoming events in your country. When you finish, we show you the events they came from.",
        },
        {
          q: "Is there a leaderboard?",
          a: "Yes, one per level: your average time and your best time, worldwide or in your country. To appear on it you need an account and to join the leaderboard.",
        },
      ],
    },
    title: "Word search",
    ogSubtitle: "Find the words from this week's events. 3 levels a day.",
    description:
      "Find the hidden words, taken from this week's events. 3 levels and 3 tries a day: can you reach the hard one?",
    levelNames: { 1: "Easy", 2: "Medium", 3: "Hard" } as Record<number, string>,
    lives: (n: number) => (n === 1 ? "1 try" : `${n} tries`),
    levelSummary: (words: number, minutes: number) => `${words} words in ${minutes} minutes`,
    howTo:
      "Drag your finger or mouse over the letters to mark each word. The words come from real events: when you find one, we show you the event.",
    lifeRule: "You get 3 tries a day and each game uses one, win or lose. If time runs out, you retry the same level.",
    start: (level: string) => `Start ${level} level`,
    starting: "Building the puzzle…",
    found: (n: number, total: number) => `${n} of ${total}`,
    timeLeft: "Time",
    wordsTitle: "To find",
    foundTitle: "Found",
    completedTitle: "Completed!",
    completedBody: (level: string) => `You completed the ${level} level, congratulations!`,
    completedTime: (time: string) => `You solved it in ${time}.`,
    playedInTitle: (language: string) => `You already played today in ${language}`,
    playedInBody: "You get 3 tries a day, in a single language. Keep playing there or come back tomorrow.",
    playedInLink: (language: string) => `Continue in ${language}`,
    continue: "Continue",
    eventsTitle: "Events behind your words",
    carouselTitle: "Your words came from these events",
    carouselSubtitle: "Up for going?",
    eventsHint: "They open in a new tab: the clock keeps running.",
    lostTitle: "Time's up",
    lostBody: (found: number, total: number) => `You found ${found} of ${total} words.`,
    nextUp: (level: string, lives: string) => `Next up: ${level} level. You have ${lives} left.`,
    retry: (level: string, lives: string) => `You can retry the ${level} level. You have ${lives} left.`,
    allDoneTitle: "You completed all 3 levels today!",
    allDoneBody: "Tomorrow brings new puzzles with this week's events.",
    noLivesTitle: "No tries left today",
    livesBack: (time: string) => `They come back at midnight (in ${time}).`,
    otherGame: "Meanwhile, try Word of the day",
    loadError: "We couldn't load the word search.",
    retryLoad: "Try again",
    actionError: "We couldn't save your move. Check your connection.",
    conflict: "The game changed in another tab; we refreshed it.",
    loginHint: "Sign in to keep your tries and progress in your account.",
    login: "Sign in",
    gridLabel: "Word search",
  },
});
